import React from 'react';
import { Link } from 'react-router-dom';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';
import { HackathonPayload } from '../../services/hackathon.service';
import { IdeaPayload } from '../../services/idea.service';
import { useSessions } from '../../hooks/useSessions';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useAuth } from '../../services/useAuth';
import { notEntryReason, voteEligibility } from '../../utils/sessions';
import { JudgeVoteCard } from './JudgeVoteCard';

/**
 * Voting on one team: a card per session the team presents in, or the reason you can't vote there yet.
 */
export const IdeaVoting = ({
    hackathonId,
    hackathon,
    idea,
    onSubmit
}: {
    // as in the URL (slug or id), shared with the other session views for caching
    hackathonId: string,
    hackathon: HackathonPayload,
    idea: IdeaPayload,
    onSubmit?: () => void
}) => {
    const { sessions, loading } = useSessions(hackathonId);
    const board = useLiveBoard(sessions.length > 0 ? hackathonId : undefined);
    const { user } = useAuth();
    const email = user ? user.email : undefined;

    if (loading && sessions.length === 0) {
        return null;
    }
    if (sessions.length === 0) {
        return null;
    }

    const containing = sessions.filter(s => s.ideas.includes(idea._id));
    if (containing.length === 0) {
        const leftOut = board.sessions.some(s => s.leftOut.includes(idea._id));
        return <Typography color="text.secondary">
            {notEntryReason(hackathon, idea._id) || (leftOut ? 'Not presenting this time: the hosts left it out, so it isn\'t being judged.' : 'This team isn\'t in a judging session yet.')}
        </Typography>;
    }

    return (
        <Stack spacing={2}>
            {
                containing.map(session => {
                    const eligibility = voteEligibility(hackathon, session, board.sessions.find(l => l._id === session._id), idea._id, email, {
                        single: sessions.length === 1,
                        votingPeriodOn: board.votingPeriodOn
                    });
                    return (
                        <Stack key={session._id} spacing={1}>
                            {containing.length > 1 && <Typography variant="subtitle2">In {session.name}</Typography>}
                            {
                                eligibility.allowed
                                    ? <JudgeVoteCard hackathon={hackathon} idea={idea} session={session} onSubmit={onSubmit} />
                                    : (
                                        <Alert severity="info" action={eligibility.inRoom &&
                                            <Button color="inherit" size="small" component={Link} to={`/h/${hackathon.slug}/s/${session.slug}`}>Open the presentation room</Button>
                                        }>{eligibility.reason}</Alert>
                                    )
                            }
                        </Stack>
                    );
                })
            }
        </Stack>
    );
}
